/**
 * Imprime um bloco de etiquetas num documento próprio, de 100 × 50 mm.
 *
 * Por que não dá para usar o `window.print()` da página: `@page` é GLOBAL —
 * não existe `@page` por classe. A aplicação declara `@page { size: A4
 * landscape }` para o roteiro e o kardex, e página NOMEADA não resolve (o
 * Chrome usa o `page:` para quebrar, mas mantém o tamanho do `@page` global).
 * Medido nos PDFs: saía MediaBox `841,9 × 595,0 pt` contra `283 × 141,7 pt`
 * do Procfit.
 *
 * A saída é um IFRAME com documento próprio, e ele é AUTOSSUFICIENTE: não
 * clona folha de estilo nenhuma da aplicação. A etiqueta já desenha tudo por
 * `style` inline (as posições vieram medidas do PDF de referência), então a
 * única coisa que faltava era a geometria da caixa — declarada aqui embaixo.
 *
 * Clonar os estilos era o que produzia papel em branco, e de duas maneiras ao
 * mesmo tempo: o `position: relative` da etiqueta vinha de uma classe do
 * Tailwind, e enquanto a folha clonada não fosse aplicada os filhos se
 * posicionavam contra a página, saíam da caixa e o `overflow: hidden` cortava
 * tudo; e a espera pela folha (que pode nunca carregar dentro do iframe)
 * deixava a impressão sair antes do layout. Sem folha externa, não há o que
 * esperar nem o que falhar.
 */
export interface OpcoesImpressao {
  /** Tamanho do papel. Padrão: a etiqueta de separação, 100 × 50 mm. */
  larguraMm?: number;
  alturaMm?: number;
  titulo?: string;
  /** Chamado depois do `print()` — que, no Chrome, só volta quando a impressão saiu da tela. */
  aoTerminar?: () => void;
}

export function imprimirEtiquetas(conteudo: HTMLElement, opcoes: OpcoesImpressao = {}): void {
  const { larguraMm = 100, alturaMm = 50, titulo = 'Etiquetas de separacao', aoTerminar } = opcoes;
  const html = conteudo.innerHTML.trim();
  if (!html) {
    aoTerminar?.();
    return; // nada para imprimir: não abre diálogo nenhum
  }

  document.getElementById('quadro-impressao-etiquetas')?.remove();

  const quadro = document.createElement('iframe');
  quadro.id = 'quadro-impressao-etiquetas';
  /*
   * Fora da tela, mas VISÍVEL e com tamanho.
   *
   * `display:none`, `visibility:hidden`, `opacity:0` ou 0 × 0 fazem o Chrome
   * pular o layout do iframe — e sem layout ele imprime branco. Jogar para
   * fora da área visível preserva o layout e não aparece para ninguém.
   */
  quadro.style.cssText = `position:fixed;left:-10000px;top:0;width:${larguraMm + 20}mm;height:${alturaMm + 30}mm;border:0;`;
  document.body.appendChild(quadro);

  const doc = quadro.contentDocument;
  if (!doc) {
    quadro.remove();
    aoTerminar?.();
    return;
  }

  doc.open();
  doc.write(`<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<title>${titulo}</title>
<style>
  /* O UNICO @page deste documento. */
  @page { size: ${larguraMm}mm ${alturaMm}mm; margin: 0; }

  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #000;
    /* Arial e a fonte do relatorio original; sem fonte externa para carregar. */
    font-family: Arial, Helvetica, sans-serif;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /*
   * A geometria da etiqueta, declarada aqui e nao herdada de classe nenhuma:
   * o conteudo e posicionado em absoluto DENTRO dela, entao o
   * position:relative precisa existir de qualquer maneira.
   */
  .etiqueta {
    position: relative !important;
    box-sizing: border-box;
    width: ${larguraMm}mm;
    /* 49.9 e nao 50: no arredondamento para pontos a altura exata empurra uma
       linha para a pagina seguinte e sai uma folha em branco por etiqueta. */
    height: ${alturaMm - 0.1}mm;
    overflow: hidden;
    background: #fff;
    display: block;
    break-after: page;
    page-break-after: always;
    break-inside: avoid;
    page-break-inside: avoid;
    box-shadow: none !important;
    border: 0 !important;
    outline: 0 !important;
  }
  .etiqueta:last-child { break-after: auto; page-break-after: auto; }

  /* A grade com espacamento e da tela; no papel cada etiqueta e uma pagina. */
  .grade-previa { display: block; }

  /* Utilitarios do Tailwind que a etiqueta usa e que aqui nao existem. */
  .tabular { font-variant-numeric: tabular-nums; }
  .font-mono { font-family: "Courier New", monospace; }
</style>
</head>
<body><div class="grade-previa">${html}</div></body>
</html>`);
  doc.close();

  let jaImprimiu = false;
  const imprimirAgora = (): void => {
    if (jaImprimiu) return;
    jaImprimiu = true;
    /*
     * O foco volta para onde estava. A etiqueta de expedição imprime sozinha
     * enquanto o operador trabalha em outra tela — sem isto, a bipada seguinte
     * no campo da conferência cairia no quadro de impressão e se perderia.
     */
    const anterior = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    quadro.contentWindow?.focus();
    quadro.contentWindow?.print();
    anterior?.focus({ preventScroll: true });
    aoTerminar?.();
    /*
     * O quadro NÃO é removido depois de imprimir — só na próxima impressão
     * (lá em cima). Remover 2 s depois imprimia BRANCO: quem ajusta a
     * obscuridade sai da prévia do Chrome para a caixa de diálogo do sistema,
     * fica mais de 2 s nas preferências do driver, e quando confirma o
     * documento já não existe (16/10/2026, CD 3201). Fora da tela, ele não
     * atrapalha ninguém.
     */
  };

  /*
   * Só imprime quando houver LAYOUT — e não quando o documento diz estar
   * pronto.
   *
   * A verificação é direta: as etiquetas existem no DOM do iframe e a
   * primeira tem altura medida. Foi a falta disso que imprimiu em branco:
   * `fonts.ready` resolvia num documento recém-escrito, a impressão saía antes
   * de o Chrome medir qualquer coisa, e a espera de reserva nunca rodava
   * porque a primeira já tinha "impresso".
   */
  const inicio = Date.now();
  const tentar = (): void => {
    const etiquetas = doc.querySelectorAll('.etiqueta');
    const primeira = etiquetas[0] as HTMLElement | undefined;
    const temLayout = !!primeira && primeira.getBoundingClientRect().height > 10;

    if (temLayout) {
      // Mais um quadro para a pintura assentar antes de abrir o diálogo.
      quadro.contentWindow?.requestAnimationFrame(imprimirAgora);
      return;
    }
    // Teto de 3 s: melhor um diálogo com o que houver do que botão que não faz
    // nada. Passado o teto, imprime assim mesmo.
    if (Date.now() - inicio > 3000) {
      imprimirAgora();
      return;
    }
    window.setTimeout(tentar, 50);
  };

  tentar();
}
